import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { api } from '@services';
import { VideosData, VideoCommentsResponse } from '@common/types/api';

type SortOrder = 'newest' | 'oldest';

export const useDeleteVideoCommentMutation = (
  videoId: number,
  commentId: number,
  sortOrder: SortOrder,
) => {
  const queryClient = useQueryClient();
  const detailKey = ['video-detail', String(videoId)];
  const commentsKey = ['video-comments', String(videoId), sortOrder];

  return useMutation(() => api.delete(`/videos/comment/${commentId}`), {
    onMutate: async () => {
      await queryClient.cancelQueries(commentsKey);
      await queryClient.cancelQueries(detailKey);

      const previousComments =
        queryClient.getQueryData<InfiniteData<VideoCommentsResponse>>(
          commentsKey,
        );
      const previousDetail = queryClient.getQueryData<VideosData>(detailKey);

      if (previousDetail) {
        queryClient.setQueryData<VideosData>(detailKey, {
          ...previousDetail,
          comments_count: Math.max(0, previousDetail.comments_count - 1),
        });
      }

      if (previousComments) {
        const newPages = previousComments.pages.map((page) => ({
          ...page,
          data: page.data.filter((c) => c.id !== commentId),
        }));

        queryClient.setQueryData<InfiniteData<VideoCommentsResponse>>(
          commentsKey,
          { ...previousComments, pages: newPages },
        );
      }

      return { previousComments, previousDetail };
    },
    onError: (_, __, ctx) => {
      if (ctx?.previousComments) {
        queryClient.setQueryData(commentsKey, ctx.previousComments);
      }
      if (ctx?.previousDetail) {
        queryClient.setQueryData(detailKey, ctx.previousDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(commentsKey);
      queryClient.invalidateQueries(detailKey);
    },
  });
};
